/** Prefer files, then item-backed files exposed by WebKit clipboard events. */
export function clipboardFiles(data: DataTransfer | null): File[] {
  const files = Array.from(data?.files ?? []);
  if (files.length) return files;
  return Array.from(data?.items ?? []).flatMap((item) => {
    const file = item.kind === "file" ? item.getAsFile() : null;
    return file ? [file] : [];
  });
}

/** The clipboard plugin returns RGBA pixels, which must be encoded for upload. */
export async function readClipboardImage(): Promise<File | null> {
  const { readImage } = await import("@tauri-apps/plugin-clipboard-manager");
  // No image is the normal result for a plain-text clipboard.
  const image = await readImage().catch(() => null);
  if (!image) return null;
  const canvas = document.createElement("canvas");
  try {
    const { width, height } = await image.size();
    if (width <= 0 || height <= 0) throw new Error("Clipboard image is empty.");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Couldn’t prepare clipboard image.");
    const pixels = new Uint8ClampedArray(await image.rgba());
    context.putImageData(new ImageData(pixels, width, height), 0, 0);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((encoded) => {
        if (encoded) resolve(encoded);
        else reject(new Error("Couldn’t encode clipboard image."));
      }, "image/png");
    });
    return new File([blob], "clipboard-image.png", { type: "image/png" });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    await image.close();
  }
}
